import { format, parse } from 'date-fns';
import { capitalize, get } from 'lodash';
import { API_ENDPOINTS } from 'src/constants/api-endpoints';
import { API_DATE_FORMAT, FRONT_DATE_FORMAT, PHOTO_PLACEHOLDER } from '../../constants/base';
import { urlToApi } from './url-utils';

export const numberWithSpaces = (x: number): string => {
  return x ? String(x).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : '0';
};

export const numberWithLeadingZero = (x: number): string => {
  return String((x < 10 ? '0' : '') + x);
};

export const fromSecondsToTimerFormat = (x: number): string => {
  const minutes = Math.floor(x / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (x % 60).toString().padStart(2, '0');

  return minutes + ':' + seconds;
};

export const formatDate = (date: string) => {
  return format(parse(date, API_DATE_FORMAT, new Date()), FRONT_DATE_FORMAT);
};

export const getFullName = (
  user: Record<string, any>,
  variant: 'full' | 'short' | 'initials' = 'full'
) => {
  if (!user) return;

  const { lastName, firstName, middleName } = user;

  switch (variant) {
    case 'short':
      return [lastName, firstName].filter(Boolean).map(capitalize).join(' ');
    case 'initials':
      return `${lastName && capitalize(lastName)} ${firstName && firstName.slice(0, 1) + '.'} ${
        middleName !== null ? middleName.slice(0, 1) + '.' : ''
      }`;
    case 'full':
    default:
      return [lastName, firstName, middleName].filter(Boolean).map(capitalize).join(' ');
  }
};

export const getFullAddress = (location: Record<string, any>, locale: string = 'ru') => {
  const region = get(location, ['region', 'title', locale]);
  const district = get(location, ['district', 'title', locale]);
  const address = get(location, ['address', locale]);
  return [region, district, address].filter(Boolean).join(', ');
};

export const filterObject = (object: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(object).filter(([_, v]) => ![null, '', undefined, 'undefined'].includes(v))
  );

export const getCertificateUrl = (uid: string) =>
  urlToApi(API_ENDPOINTS.CERTIFICATE.FIND_FILE.replace(':uid', uid));

export const getTimeLeft = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${mins > 0 ? mins + ' минут ' : ''}${secs > 0 ? secs + ' секунд' : ''}`;
};

export const getProfileImage = (photo: string | null | undefined) =>
  photo ? `data:image/jpeg;base64,${photo}` : PHOTO_PLACEHOLDER;

export const stripHtml = (html: string) => {
  let tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

export const alphabetLetterByIndex = (idx: number) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return alphabet[idx];
};

export const getTranslatedValue = (obj: Record<string, any>, locale: string) => {
  if (!obj) return '';

  return obj[locale] || obj.oz || obj.uz || obj.ru || '';
};

// fill empty values in object with translations
export const fillEmptyTranslations = (obj: Record<string, any>, locale: string) => {
  const translatedValue = getTranslatedValue(obj, locale);
  return Object.keys(obj).reduce((acc: Record<string, any>, key: string) => {
    acc[key] = obj[key] || translatedValue;
    return acc;
  }, {});
};
